import React, { useEffect, useState, useCallback } from "react";
import {
  Plus, Upload, Search, Trash2, Pencil, Send, X, Users,
  Phone, Mail, Building2, Tag, Sparkles, MessageCircle, ExternalLink, RefreshCw, ChevronDown,
  LayoutGrid, Table2
} from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

async function api(method, path, body) {
  const opts = { method, headers: { "Content-Type": "application/json" }, credentials: "include" };
  if (body) opts.body = JSON.stringify(body);
  const r = await fetch(`${API}/api/admin/contacts${path}`, opts);
  if (!r.ok) { const e = await r.json().catch(() => ({})); throw new Error(e.detail || `Error ${r.status}`); }
  return r.json();
}

const inputCls = "w-full rounded-xl border border-[#26201625] bg-white/80 px-3.5 py-2.5 text-sm text-[#262016] outline-none focus:border-[#B5793F] placeholder-[#8A7E6B]";
const labelCls = "block text-[11px] uppercase tracking-[0.14em] text-[#8A7E6B] font-medium mb-1.5";
const btnPrimary = { background: "linear-gradient(135deg, #262016 0%, #3B301F 100%)", border: "1px solid #B5793F" };

const TAG_COLORS = ["#3B82F6", "#10B981", "#F59E0B", "#EC4899", "#8B5CF6", "#EF4444", "#14B8A6", "#F97316", "#6366F1", "#84CC16"];

export default function ContactsSection() {
  const [contacts, setContacts] = useState([]);
  const [tags, setTags] = useState([]);
  const [stats, setStats] = useState(null);
  const [search, setSearch] = useState("");
  const [filterTag, setFilterTag] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [importText, setImportText] = useState("");
  const [importTags, setImportTags] = useState([]);
  const [importSource, setImportSource] = useState("");
  const [viewMode, setViewMode] = useState("table"); // "cards" | "table"
  const [editingContact, setEditingContact] = useState(null);
  // Draft modal
  const [draftModal, setDraftModal] = useState(null);
  const [draftLoading, setDraftLoading] = useState(false);
  // New tag input
  const [newTagName, setNewTagName] = useState("");
  // Form fields
  const [form, setForm] = useState({ name: "", email: "", phone: "", company: "", role: "", business_info: "", notes: "", tags: [], source: "" });

  const loadContacts = useCallback(() => {
    const params = [];
    if (filterTag) params.push(`tag=${encodeURIComponent(filterTag)}`);
    if (search) params.push(`search=${encodeURIComponent(search)}`);
    const q = params.length ? `?${params.join("&")}` : "";
    api("GET", q).then(setContacts).catch(() => setContacts([]));
  }, [filterTag, search]);

  const loadTags = useCallback(() => { api("GET", "/tags").then(setTags).catch(() => setTags([])); }, []);
  const loadStats = useCallback(() => { api("GET", "/stats").then(setStats).catch(() => {}); }, []);

  useEffect(() => { loadTags(); loadStats(); }, [loadTags, loadStats]);
  useEffect(() => { loadContacts(); }, [loadContacts]);

  const resetForm = () => setForm({ name: "", email: "", phone: "", company: "", role: "", business_info: "", notes: "", tags: [], source: "" });

  const createTag = async () => {
    if (!newTagName.trim()) return;
    try {
      const t = await api("POST", "/tags", { name: newTagName.trim(), color: TAG_COLORS[tags.length % TAG_COLORS.length] });
      setTags(prev => [...prev.filter(x => x.id !== t.id), t]);
      setNewTagName("");
      toast.success(`Tag "${t.name}" created`);
    } catch (e) { toast.error(e.message); }
  };

  const deleteTag = async (tagId) => {
    if (!window.confirm("Delete this tag? It will be removed from all contacts.")) return;
    try {
      await api("DELETE", `/tags/${tagId}`);
      loadTags(); loadContacts();
      toast.success("Tag deleted");
    } catch (e) { toast.error(e.message); }
  };

  const toggleFormTag = (tagName) => {
    setForm(prev => ({
      ...prev,
      tags: prev.tags.includes(tagName) ? prev.tags.filter(t => t !== tagName) : [...prev.tags, tagName]
    }));
  };

  const saveContact = async () => {
    try {
      if (editingContact) {
        await api("PUT", `/${editingContact.id}`, form);
        toast.success("Contact updated");
      } else {
        await api("POST", "", form);
        toast.success("Contact added");
      }
      resetForm();
      setShowForm(false);
      setEditingContact(null);
      loadContacts(); loadStats();
    } catch (e) { toast.error(e.message); }
  };

  const editContact = (c) => {
    setForm({ name: c.name, email: c.email, phone: c.phone, company: c.company, role: c.role, business_info: c.business_info, notes: c.notes, tags: c.tags || [], source: c.source || "" });
    setEditingContact(c);
    setShowForm(true);
  };

  const deleteContact = async (id) => {
    if (!window.confirm("Delete this contact?")) return;
    try {
      await api("DELETE", `/${id}`);
      loadContacts(); loadStats();
    } catch (e) { toast.error(e.message); }
  };

  const [importing, setImporting] = useState(false);

  const importContacts = async () => {
    if (!importText.trim()) return toast.error("Paste contact data");
    setImporting(true);
    try {
      const d = await api("POST", "/import", { data: importText, tags: importTags, source: importSource || "CSV Import" });
      if (d.status === "started" || d.status === "running") {
        toast.success(d.message || `Importing ${d.total} contacts in background...`);
        // Poll for status
        const poll = setInterval(async () => {
          try {
            const s = await api("GET", "/import-status");
            if (!s.running && s.done) {
              clearInterval(poll);
              const parts = [`${s.added} contacts added`];
              if (s.skipped_phone) parts.push(`${s.skipped_phone} duplicate phones`);
              if (s.skipped_email) parts.push(`${s.skipped_email} duplicate emails`);
              if (s.errors) parts.push(`${s.errors} invalid rows`);
              toast.success(`Import complete: ${parts.join(", ")}`);
              setImporting(false);
              setImportText("");
              setShowImport(false);
              setImportTags([]);
              setImportSource("");
              loadContacts(); loadStats();
            }
          } catch { clearInterval(poll); setImporting(false); }
        }, 2000);
      } else {
        const parts = [`${d.added} contacts added`];
        if (d.skipped_phone) parts.push(`${d.skipped_phone} duplicate phones`);
        if (d.skipped_email) parts.push(`${d.skipped_email} duplicate emails`);
        if (d.errors) parts.push(`${d.errors} invalid rows`);
        toast.success(parts.join(", "));
        setImporting(false);
        setImportText("");
        setShowImport(false);
        setImportTags([]);
        setImportSource("");
        loadContacts(); loadStats();
      }
    } catch (e) { toast.error(e.message); setImporting(false); }
  };

  // AI Draft
  const openAiDraft = async (contact, purpose = "general outreach") => {
    setDraftModal({ contact, subject: "", body_html: "", body_text: "", purpose });
    setDraftLoading(true);
    try {
      const d = await api("POST", `/${contact.id}/ai-draft`, { purpose });
      setDraftModal(prev => ({ ...prev, subject: d.subject, body_html: d.body_html, body_text: d.body_text }));
    } catch (e) { toast.error(e.message); setDraftModal(null); }
    setDraftLoading(false);
  };

  const sendDraftEmail = async () => {
    if (!draftModal) return;
    try {
      const d = await api("POST", `/${draftModal.contact.id}/send-email`, {
        subject: draftModal.subject, body_html: draftModal.body_html, body_text: draftModal.body_text
      });
      if (d.sent) { toast.success(`Email sent to ${draftModal.contact.email}`); setDraftModal(null); loadContacts(); }
      else toast.error(d.message || "Send failed");
    } catch (e) { toast.error(e.message); }
  };

  return (
    <div data-testid="contacts-section">
      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6" data-testid="contacts-stats">
          <div className="bg-white rounded-2xl p-4 border border-[#d4cbb8]">
            <div className="flex items-center gap-2 text-[#8A7E6B] text-xs font-semibold uppercase tracking-wider mb-1"><Users size={13} /> Total</div>
            <p className="text-2xl font-light text-[#262016]">{stats.total}</p>
          </div>
          <div className="bg-white rounded-2xl p-4 border border-[#d4cbb8]">
            <div className="flex items-center gap-2 text-blue-600 text-xs font-semibold uppercase tracking-wider mb-1"><Mail size={13} /> With Email</div>
            <p className="text-2xl font-light text-[#262016]">{stats.with_email}</p>
          </div>
          <div className="bg-white rounded-2xl p-4 border border-[#d4cbb8]">
            <div className="flex items-center gap-2 text-green-600 text-xs font-semibold uppercase tracking-wider mb-1"><Phone size={13} /> With Phone</div>
            <p className="text-2xl font-light text-[#262016]">{stats.with_phone}</p>
          </div>
          <div className="bg-white rounded-2xl p-4 border border-[#d4cbb8]">
            <div className="flex items-center gap-2 text-purple-600 text-xs font-semibold uppercase tracking-wider mb-1"><Send size={13} /> Messaged</div>
            <p className="text-2xl font-light text-[#262016]">{stats.messaged}</p>
          </div>
        </div>
      )}

      {/* Tags bar */}
      <div className="flex flex-wrap items-center gap-2 mb-4" data-testid="contacts-tags-bar">
        <button onClick={() => setFilterTag("")}
                className="px-3 py-1 rounded-full text-xs font-semibold uppercase tracking-wide transition-all"
                style={{ background: !filterTag ? "#262016" : "transparent", color: !filterTag ? "#EDE7D9" : "#5C5042", border: `1px solid ${!filterTag ? "#262016" : "rgba(38,32,22,0.2)"}` }}>
          All
        </button>
        {tags.map(t => (
          <div key={t.id} className="flex items-center gap-0.5">
            <button onClick={() => setFilterTag(filterTag === t.name ? "" : t.name)}
                    className="px-3 py-1 rounded-l-full text-xs font-semibold transition-all"
                    style={{
                      background: filterTag === t.name ? t.color : "transparent",
                      color: filterTag === t.name ? "#fff" : t.color,
                      border: `1px solid ${t.color}`, borderRight: "none"
                    }} data-testid={`tag-filter-${t.id}`}>
              {t.name}{stats?.tag_counts?.[t.name] > 0 ? ` (${stats.tag_counts[t.name]})` : ""}
            </button>
            <button onClick={() => deleteTag(t.id)} title="Delete tag"
                    className="px-1.5 py-1 rounded-r-full text-xs transition-all hover:bg-red-50"
                    style={{ border: `1px solid ${t.color}`, color: t.color }}>
              <X size={10} />
            </button>
          </div>
        ))}
        <div className="flex items-center gap-1 ml-2">
          <input value={newTagName} onChange={e => setNewTagName(e.target.value)}
                 onKeyDown={e => e.key === "Enter" && createTag()}
                 placeholder="New tag…" data-testid="new-tag-input"
                 className="w-24 px-2 py-1 rounded-full border border-dashed border-[#26201630] text-xs text-[#262016] outline-none focus:border-[#B5793F]" />
          {newTagName && <button onClick={createTag} className="text-xs text-[#B5793F] font-semibold">Add</button>}
        </div>
      </div>

      {/* Search + Actions */}
      <div className="flex flex-wrap items-center gap-3 mb-6">
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8A7E6B]" />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search name, email, phone, company…"
                 className="w-full pl-9 pr-3 py-2.5 rounded-full border border-[#26201625] text-sm text-[#262016] outline-none focus:border-[#B5793F]" data-testid="contacts-search" />
        </div>
        <button onClick={() => { resetForm(); setEditingContact(null); setShowForm(!showForm); }} data-testid="add-contact-btn"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold text-[#F6F1E5]" style={btnPrimary}>
          <Plus size={14} /> Add Contact
        </button>
        <button onClick={() => setShowImport(!showImport)} data-testid="import-contacts-btn"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold border border-[#26201625] text-[#262016] hover:border-[#B5793F]">
          <Upload size={14} /> Import CSV
        </button>
        <div className="flex rounded-full border border-[#26201625] overflow-hidden ml-auto" data-testid="view-toggle">
          <button onClick={() => setViewMode("cards")}
                  className={`px-3 py-2 text-xs font-semibold ${viewMode === "cards" ? "bg-[#262016] text-[#EDE7D9]" : "text-[#8A7E6B] hover:bg-[#26201608]"}`}>
            <LayoutGrid size={14} />
          </button>
          <button onClick={() => setViewMode("table")}
                  className={`px-3 py-2 text-xs font-semibold ${viewMode === "table" ? "bg-[#262016] text-[#EDE7D9]" : "text-[#8A7E6B] hover:bg-[#26201608]"}`}>
            <Table2 size={14} />
          </button>
        </div>
      </div>

      {/* Add/Edit Form */}
      {showForm && (
        <div className="bg-white rounded-2xl p-6 border border-[#d4cbb8] mb-6" data-testid="contact-form">
          <p className="text-sm font-semibold text-[#262016] mb-4">{editingContact ? "Edit Contact" : "Add Contact"}</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div><label className={labelCls}>Name</label><input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} className={inputCls} placeholder="John Doe" data-testid="contact-name-input" /></div>
            <div><label className={labelCls}>Email</label><input value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))} className={inputCls} placeholder="john@example.com" data-testid="contact-email-input" /></div>
            <div><label className={labelCls}>Phone</label><input value={form.phone} onChange={e => setForm(p => ({ ...p, phone: e.target.value }))} className={inputCls} placeholder="+91 98765 43210" data-testid="contact-phone-input" /></div>
            <div><label className={labelCls}>Company</label><input value={form.company} onChange={e => setForm(p => ({ ...p, company: e.target.value }))} className={inputCls} placeholder="Acme Corp" data-testid="contact-company-input" /></div>
            <div><label className={labelCls}>Role / Title</label><input value={form.role} onChange={e => setForm(p => ({ ...p, role: e.target.value }))} className={inputCls} placeholder="Marketing Head" data-testid="contact-role-input" /></div>
            <div><label className={labelCls}>Source</label><input value={form.source} onChange={e => setForm(p => ({ ...p, source: e.target.value }))} className={inputCls} placeholder="e.g., LinkedIn, Trade Show, Referral" data-testid="contact-source-input" /></div>
            <div className="sm:col-span-2"><label className={labelCls}>Business Info (for AI research)</label><textarea value={form.business_info} onChange={e => setForm(p => ({ ...p, business_info: e.target.value }))} className={inputCls + " h-20 resize-none"} placeholder="What does their business do? Any context that helps AI draft a message…" data-testid="contact-biz-input" /></div>
            <div className="sm:col-span-2"><label className={labelCls}>Notes</label><textarea value={form.notes} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))} className={inputCls + " h-16 resize-none"} placeholder="Personal notes…" data-testid="contact-notes-input" /></div>
          </div>
          {/* Tags */}
          <div className="mt-4">
            <label className={labelCls}>Tags</label>
            <div className="flex flex-wrap gap-2">
              {tags.map(t => (
                <button key={t.id} onClick={() => toggleFormTag(t.name)}
                        className="px-3 py-1 rounded-full text-xs font-semibold transition-all"
                        style={{
                          background: form.tags.includes(t.name) ? t.color : "transparent",
                          color: form.tags.includes(t.name) ? "#fff" : t.color,
                          border: `1px solid ${t.color}`,
                        }} data-testid={`form-tag-${t.id}`}>
                  {t.name}
                </button>
              ))}
              {tags.length === 0 && <span className="text-xs text-[#8A7E6B]">Create tags above first</span>}
            </div>
          </div>
          <div className="flex gap-3 mt-5">
            <button onClick={saveContact} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold text-[#F6F1E5]" style={btnPrimary} data-testid="contact-save-btn">
              {editingContact ? "Update" : "Add"} Contact
            </button>
            <button onClick={() => { setShowForm(false); setEditingContact(null); resetForm(); }} className="text-sm text-[#8A7E6B] hover:text-[#262016]">Cancel</button>
          </div>
        </div>
      )}

      {/* Import */}
      {showImport && (
        <div className="bg-white rounded-2xl p-6 border border-[#d4cbb8] mb-6" data-testid="import-form">
          <p className="text-sm font-semibold text-[#262016] mb-2">Import Contacts</p>
          <p className="text-[11px] text-[#8A7E6B] mb-3">
            Paste CSV data with headers (name, email, phone, company, role) or just email/phone lists. Duplicates by phone number or email are auto-skipped.
          </p>
          <textarea value={importText} onChange={e => setImportText(e.target.value)}
                    className="w-full h-36 rounded-xl border border-[#26201625] p-3 text-sm text-[#262016] font-mono resize-none focus:outline-none focus:border-[#B5793F] mb-3"
                    placeholder={"Supports Google Contacts CSV, or simple format:\nname,email,phone,company\nJohn Doe,john@email.com,+91 98765 43210,Acme Corp"} data-testid="import-textarea" />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-3">
            <div>
              <label className={labelCls}>Source (where did these contacts come from?)</label>
              <input value={importSource} onChange={e => setImportSource(e.target.value)} className={inputCls}
                     placeholder="e.g., LinkedIn Export, Trade Show 2024, Wedding Guest List" data-testid="import-source-input" />
            </div>
          </div>
          <label className={labelCls}>Apply tags to imported contacts</label>
          <div className="flex flex-wrap gap-2 mb-4">
            {tags.map(t => (
              <button key={t.id} onClick={() => setImportTags(prev => prev.includes(t.name) ? prev.filter(x => x !== t.name) : [...prev, t.name])}
                      className="px-3 py-1 rounded-full text-xs font-semibold transition-all"
                      style={{ background: importTags.includes(t.name) ? t.color : "transparent", color: importTags.includes(t.name) ? "#fff" : t.color, border: `1px solid ${t.color}` }}>
                {t.name}
              </button>
            ))}
          </div>
          <div className="flex gap-3">
            <button onClick={importContacts} disabled={importing} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold text-[#F6F1E5] disabled:opacity-50" style={btnPrimary} data-testid="import-submit-btn">
              {importing ? <><RefreshCw size={13} className="animate-spin" /> Importing...</> : <><Upload size={13} /> Import</>}
            </button>
            <label className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold border border-[#26201625] text-[#262016] hover:border-[#B5793F] cursor-pointer">
              <Upload size={13} /> Upload File
              <input type="file" accept=".csv,.txt" className="hidden" data-testid="import-file-input"
                     onChange={async (e) => { const f = e.target.files?.[0]; if (f) { setImportText(await f.text()); } e.target.value = ""; }} />
            </label>
            <button onClick={() => { setShowImport(false); setImportText(""); setImportTags([]); }} className="text-sm text-[#8A7E6B]">Cancel</button>
          </div>
        </div>
      )}

      {/* Contact List */}
      <div data-testid="contacts-list">
        {contacts.length === 0 && (
          <div className="bg-white/55 rounded-2xl p-8 text-center border border-[#d4cbb8]">
            <Users className="mx-auto mb-3 text-[#8A7E6B]" size={22} />
            <p className="text-sm text-[#8A7E6B]">{search || filterTag ? "No contacts match your filter" : "Add contacts to get started"}</p>
          </div>
        )}

        {/* Table View */}
        {contacts.length > 0 && viewMode === "table" && (
          <div className="bg-white rounded-2xl border border-[#d4cbb8] overflow-x-auto" data-testid="contacts-table">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[#d4cbb8] text-[11px] uppercase tracking-[0.12em] text-[#8A7E6B] font-medium">
                  <th className="text-left px-4 py-3">Name</th>
                  <th className="text-left px-4 py-3">Email</th>
                  <th className="text-left px-4 py-3">Phone</th>
                  <th className="text-left px-4 py-3">Company</th>
                  <th className="text-left px-4 py-3">Tags</th>
                  <th className="text-left px-4 py-3">Source</th>
                  <th className="text-left px-4 py-3">Last Contacted</th>
                  <th className="text-left px-4 py-3">Msgs</th>
                  <th className="text-left px-4 py-3">Added</th>
                  <th className="text-right px-4 py-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {contacts.map(c => (
                  <tr key={c.id} className="border-b border-[#26201610] hover:bg-[#FDFBF6] transition-colors" data-testid={`table-row-${c.id}`}>
                    <td className="px-4 py-3 font-medium text-[#262016] whitespace-nowrap">{c.name || "—"}</td>
                    <td className="px-4 py-3 text-[#5C5042]">{c.email || "—"}</td>
                    <td className="px-4 py-3 text-[#5C5042] whitespace-nowrap">{c.phone || "—"}</td>
                    <td className="px-4 py-3 text-[#5C5042]">
                      {c.company ? <span>{c.company}{c.role ? <span className="text-[#8A7E6B]"> · {c.role}</span> : ""}</span> : "—"}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-1">
                        {(c.tags || []).map(t => {
                          const tagObj = tags.find(x => x.name === t);
                          return <span key={t} className="px-2 py-0.5 rounded-full text-[10px] font-semibold" style={{ background: (tagObj?.color || "#8A7E6B") + "18", color: tagObj?.color || "#8A7E6B" }}>{t}</span>;
                        })}
                        {(!c.tags || c.tags.length === 0) && <span className="text-[#8A7E6B]">—</span>}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-[#8A7E6B] text-[12px]">{c.source || "—"}</td>
                    <td className="px-4 py-3 text-[#8A7E6B] text-[12px] whitespace-nowrap">
                      {c.last_contacted ? new Date(c.last_contacted).toLocaleDateString() : "Never"}
                    </td>
                    <td className="px-4 py-3 text-[#5C5042] text-center">{c.messages_sent || 0}</td>
                    <td className="px-4 py-3 text-[#8A7E6B] text-[12px] whitespace-nowrap">
                      {c.created_at ? new Date(c.created_at).toLocaleDateString() : "—"}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        {(c.company || c.business_info) && (
                          <button onClick={() => openAiDraft(c)} title="AI Draft" data-testid={`table-ai-${c.id}`}
                                  className="w-7 h-7 rounded-full border border-purple-200 flex items-center justify-center text-purple-600 hover:bg-purple-50">
                            <Sparkles size={11} />
                          </button>
                        )}
                        {c.email && (
                          <button onClick={() => openAiDraft(c, "email outreach")} title="Email" data-testid={`table-email-${c.id}`}
                                  className="w-7 h-7 rounded-full border border-blue-200 flex items-center justify-center text-blue-600 hover:bg-blue-50">
                            <Mail size={11} />
                          </button>
                        )}
                        {c.whatsapp_link && (
                          <a href={c.whatsapp_link} target="_blank" rel="noopener noreferrer" title="WhatsApp" data-testid={`table-wa-${c.id}`}
                             className="w-7 h-7 rounded-full border border-green-200 flex items-center justify-center text-green-600 hover:bg-green-50">
                            <MessageCircle size={11} />
                          </a>
                        )}
                        <button onClick={() => editContact(c)} title="Edit" data-testid={`table-edit-${c.id}`}
                                className="w-7 h-7 rounded-full border border-[#26201620] flex items-center justify-center text-[#8A7E6B] hover:text-[#262016]">
                          <Pencil size={10} />
                        </button>
                        <button onClick={() => deleteContact(c.id)} title="Delete" data-testid={`table-delete-${c.id}`}
                                className="w-7 h-7 rounded-full border border-[#26201620] flex items-center justify-center text-[#8A7E6B] hover:text-red-700">
                          <Trash2 size={10} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Card View */}
        {contacts.length > 0 && viewMode === "cards" && contacts.map(c => (
          <div key={c.id} className="bg-white rounded-2xl px-5 py-4 border border-[#d4cbb8] shadow-sm" style={{ marginBottom: "14px" }} data-testid={`contact-row-${c.id}`}>
            <div className="flex items-start gap-4">
              <span className="w-10 h-10 rounded-full flex items-center justify-center shrink-0 text-white text-sm font-bold" style={{ background: "#262016" }}>
                {(c.name || c.email || "?")[0].toUpperCase()}
              </span>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="text-sm font-semibold text-[#262016]">{c.name || c.email || c.phone}</p>
                  {c.tags?.map(t => {
                    const tagObj = tags.find(x => x.name === t);
                    return <span key={t} className="px-2 py-0.5 rounded-full text-[10px] font-semibold" style={{ background: (tagObj?.color || "#8A7E6B") + "18", color: tagObj?.color || "#8A7E6B" }}>{t}</span>;
                  })}
                </div>
                <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1 text-[12px] text-[#8A7E6B]">
                  {c.email && <span className="flex items-center gap-1"><Mail size={11} /> {c.email}</span>}
                  {c.phone && <span className="flex items-center gap-1"><Phone size={11} /> {c.phone}</span>}
                  {c.company && <span className="flex items-center gap-1"><Building2 size={11} /> {c.company}{c.role ? ` · ${c.role}` : ""}</span>}
                </div>
                {c.business_info && <p className="text-[12px] text-[#5C5042] mt-1.5 line-clamp-2">{c.business_info}</p>}
                {c.notes && <p className="text-[12px] text-[#8A7E6B] mt-1 italic line-clamp-1">{c.notes}</p>}
                {(c.source || c.messages_sent > 0) && (
                  <div className="flex flex-wrap gap-x-3 mt-1.5 text-[11px]">
                    {c.source && <span className="text-[#B5793F]">Source: {c.source}</span>}
                    {c.messages_sent > 0 && <span className="text-[#8A7E6B]">{c.messages_sent} message{c.messages_sent > 1 ? "s" : ""} sent{c.last_contacted ? ` · Last: ${new Date(c.last_contacted).toLocaleDateString()}` : ""}</span>}
                  </div>
                )}
              </div>
              <div className="flex items-center gap-1.5 shrink-0">
                {(c.company || c.business_info) && (
                  <button onClick={() => openAiDraft(c)} title="AI Research & Draft" data-testid={`contact-ai-${c.id}`}
                          className="w-8 h-8 rounded-full border border-purple-200 flex items-center justify-center text-purple-600 hover:bg-purple-50">
                    <Sparkles size={13} />
                  </button>
                )}
                {c.email && (
                  <button onClick={() => openAiDraft(c, "email outreach")} title="Draft & send email" data-testid={`contact-email-${c.id}`}
                          className="w-8 h-8 rounded-full border border-blue-200 flex items-center justify-center text-blue-600 hover:bg-blue-50">
                    <Mail size={13} />
                  </button>
                )}
                {c.whatsapp_link && (
                  <a href={c.whatsapp_link} target="_blank" rel="noopener noreferrer" title="Open WhatsApp" data-testid={`contact-wa-${c.id}`}
                     className="w-8 h-8 rounded-full border border-green-200 flex items-center justify-center text-green-600 hover:bg-green-50">
                    <MessageCircle size={13} />
                  </a>
                )}
                <button onClick={() => editContact(c)} title="Edit" data-testid={`contact-edit-${c.id}`}
                        className="w-8 h-8 rounded-full border border-[#26201620] flex items-center justify-center text-[#8A7E6B] hover:text-[#262016]">
                  <Pencil size={12} />
                </button>
                <button onClick={() => deleteContact(c.id)} title="Delete" data-testid={`contact-delete-${c.id}`}
                        className="w-8 h-8 rounded-full border border-[#26201620] flex items-center justify-center text-[#8A7E6B] hover:text-red-700 hover:border-red-700">
                  <Trash2 size={12} />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* AI Draft Modal */}
      {draftModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={() => setDraftModal(null)}>
          <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[85vh] overflow-y-auto m-4 shadow-2xl" onClick={e => e.stopPropagation()} data-testid="ai-draft-modal">
            <div className="sticky top-0 bg-white border-b border-[#d4cbb8] px-6 py-4 flex items-center justify-between rounded-t-2xl">
              <div>
                <p className="text-sm font-semibold text-[#262016]">AI Draft for {draftModal.contact.name || draftModal.contact.email}</p>
                <p className="text-[11px] text-[#8A7E6B]">
                  {draftModal.contact.company ? `${draftModal.contact.company}${draftModal.contact.role ? ` · ${draftModal.contact.role}` : ""}` : "Edit and send"}
                </p>
              </div>
              <button onClick={() => setDraftModal(null)} className="w-8 h-8 rounded-full border border-[#26201620] flex items-center justify-center text-[#8A7E6B] hover:text-[#262016]">
                <X size={14} />
              </button>
            </div>
            {draftLoading ? (
              <div className="p-8 text-center">
                <RefreshCw size={20} className="animate-spin mx-auto mb-3 text-[#B5793F]" />
                <p className="text-sm text-[#8A7E6B]">Researching & drafting...</p>
              </div>
            ) : (
              <div className="p-6">
                <label className={labelCls}>Subject</label>
                <input value={draftModal.subject} onChange={e => setDraftModal(p => ({ ...p, subject: e.target.value }))}
                       className={inputCls + " mb-4"} data-testid="draft-subject" />

                <label className={labelCls}>Message (HTML)</label>
                <textarea value={draftModal.body_html} onChange={e => setDraftModal(p => ({ ...p, body_html: e.target.value }))}
                          className={inputCls + " h-36 font-mono resize-none mb-3"} data-testid="draft-body" />

                <label className={labelCls}>Preview</label>
                <div className="rounded-xl border border-[#26201625] p-4 bg-[#FDFBF6] mb-5 text-sm" data-testid="draft-preview"
                     dangerouslySetInnerHTML={{ __html: draftModal.body_html }} />

                <div className="flex gap-3 justify-end">
                  <button onClick={() => openAiDraft(draftModal.contact, draftModal.purpose)}
                          className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold border border-purple-300 text-purple-600 hover:bg-purple-50" data-testid="draft-regenerate-btn">
                    <Sparkles size={12} /> Regenerate
                  </button>
                  {draftModal.contact.whatsapp_link && (
                    <a href={`${draftModal.contact.whatsapp_link}?text=${encodeURIComponent(draftModal.body_text || "")}`}
                       target="_blank" rel="noopener noreferrer"
                       className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold border border-green-300 text-green-600 hover:bg-green-50" data-testid="draft-wa-btn">
                      <MessageCircle size={12} /> Send via WhatsApp
                    </a>
                  )}
                  {draftModal.contact.email && (
                    <button onClick={sendDraftEmail}
                            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold text-[#F6F1E5]" style={btnPrimary} data-testid="draft-send-email-btn">
                      <Send size={13} /> Send Email
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
